import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet } from 'lucide-react';
import { FullScreenLayout } from '../../routes/layouts/FullScreenLayout';
import { FullScreenHeader } from '@/components/shared/layout';
import { Button } from '@/components/shared/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useClassDetail } from '@/data/queries/useClasses';
import { classDetailDtoToClassItem } from '@/utils/classUtils';
import { useParticipantStats } from '@/data/queries/useParticipantStats';
import { useScenarioStats } from '@/data/queries/useScenarioStats';
import { downloadStatisticsExcel, type LabStatsQueryParams } from '@/services/classLabPlatformService';

type TabType = 'curriculum' | 'evaluation' | 'practice-status' | 'ranking' | 'statistics';
type StatisticsSubTab = 'participants' | 'scenarios';

/**
 * 밀리초를 시간 형식으로 변환 (예: 111000ms → "1분 51초")
 */
function formatPlayTime(milliseconds: number | null | undefined, t: (key: string) => string): string {
    if (milliseconds == null || milliseconds === 0) return `0${t('units.seconds')}`;
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}${t('units.hours')}`);
    if (minutes > 0) parts.push(`${minutes}${t('units.minutes')}`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}${t('units.seconds')}`);
    
    return parts.join(' ');
}

export default function PracticeStatistics(): React.ReactElement {
    const { t, i18n } = useTranslation('class');
    const { classId: classIdParam } = useParams<{ classId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { getCurrentRole } = useAuth();

    const role = getCurrentRole();
    const basePath = role === 'master' ? '/master' : '/admin';
    const classId = parseInt((classIdParam ?? '').trim(), 10);
    const currentTab = searchParams.get('tab') || 'statistics';

    const [statisticsTab, setStatisticsTab] = useState<StatisticsSubTab>('participants');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

    const {
        data: classDetailDto,
        isError: isClassError,
        isLoading: isClassLoading,
    } = useClassDetail(classId, {
        enabled: classId > 0,
    });

    const classItem = classDetailDto ? classDetailDtoToClassItem(classDetailDto) : null;

    const statsQueryParams = useMemo((): LabStatsQueryParams => {
        const params: LabStatsQueryParams = {};
        if (startDate) {
            params.startDate = startDate;
        }
        if (endDate) {
            params.endDate = endDate;
        }
        return params;
    }, [startDate, endDate]);

    // 참가자 통계 데이터 조회
    const {
        data: participantStatsData,
        isError: isParticipantStatsError,
        isLoading: isParticipantStatsLoading,
    } = useParticipantStats(classId, statsQueryParams, {
        enabled: classId > 0,
    });

    // 시나리오 통계 데이터 조회
    const {
        data: scenarioStatsData,
        isError: isScenarioStatsError,
        isLoading: isScenarioStatsLoading,
    } = useScenarioStats(classId, statsQueryParams, {
        enabled: classId > 0 && statisticsTab === 'scenarios',
    });

    const backToClassDetailPath = (): string => {
        return `${basePath}/class-management/${classId}`;
    };

    const participants = participantStatsData?.userList ?? [];

    // 통계 카드 데이터
    const stats = useMemo(() => {
        if (!participantStatsData?.stats) {
            return {
                totalParticipants: 0,
                passCount: 0,
                passRate: 0,
                totalPlayCount: 0,
                totalPlayTime: '0시간 0분 0초',
            };
        }

        const { total_participants, pass_count, total_play_count, total_play_time } = participantStatsData.stats;
        const passRate = total_participants > 0 ? Math.round((pass_count / total_participants) * 100) : 0;

        return {
            totalParticipants: total_participants,
            passCount: pass_count,
            passRate,
            totalPlayCount: total_play_count,
            totalPlayTime: formatPlayTime(total_play_time, t),
        };
    }, [participantStatsData]);

    const handleTabChange = (tab: TabType) => {
        if (tab === 'curriculum') {
            if (classItem?.curriculum?.length) {
                const firstSession = classItem.curriculum[0];
                navigate(`${basePath}/class-management/${classId}/curriculum/${firstSession.id}`);
            }
        } else if (tab === 'evaluation') {
            navigate(`${basePath}/class-management/${classId}/evaluation`);
        } else if (tab === 'practice-status') {
            navigate(`${basePath}/class-management/${classId}/practice-status`);
        } else if (tab === 'statistics') {
            return;
        } else if (tab === 'ranking') {
            navigate(`${basePath}/class-management/${classId}/ranking`);
        } else {
            navigate(`${basePath}/class-management/${classId}/session-management?tab=${tab}`);
        }
    };

    const handleExcelDownload = async () => {
        if (isDownloadingExcel || !classItem) return;

        try {
            setIsDownloadingExcel(true);

            // 파일명 생성: 실습통계_클래스명_2026-08-13.xlsx
            const safeClassName = classItem.title.replace(/[/\\?%*:|"<>]/g, '_').slice(0, 40);
            const timestamp = new Date().toISOString().slice(0, 10);
            const filename = `${t('statistics.excelFilePrefix')}_${safeClassName}_${timestamp}.xlsx`;

            await downloadStatisticsExcel(classId, filename, statsQueryParams, undefined, i18n.language);
        } catch (error) {
            console.error(t('statistics.excelDownloadError'), error);
            alert(t('statistics.excelDownloadFailed'));
        } finally {
            setIsDownloadingExcel(false);
        }
    };

    const tabs = [
        { id: 'curriculum', label: t('session.tabCurriculum') },
        { id: 'evaluation', label: t('session.tabEvaluation') },
        { id: 'practice-status', label: t('session.tabPracticeStatus') },
        { id: 'ranking', label: t('session.tabRanking') },
        { id: 'statistics', label: t('session.tabStatistics') },
    ];

    const renderCenterContent = () => {
        return (
            <div className="flex gap-2">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id as TabType)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            currentTab === tab.id
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        );
    };

    const renderRightContent = () => {
        return (
            <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">{t('common.dateRange')}</span>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder={t('common.datePlaceholder')}
                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32"
                />
                <span className="text-gray-600">~</span>
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder={t('common.datePlaceholder')}
                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32"
                />
            </div>
        );
    };

    if (isClassError || isClassLoading || !classItem) {
        return (
            <FullScreenLayout
                header={
                    <FullScreenHeader
                        title={classItem?.title || t('statistics.title')}
                        onBack={() => navigate(backToClassDetailPath())}
                        centerContent={renderCenterContent()}
                    />
                }
            >
                <div className="flex items-center justify-center h-full text-gray-500">
                    {isClassLoading ? t('statistics.loadingData') : t('statistics.loadError')}
                </div>
            </FullScreenLayout>
        );
    }

    return (
        <FullScreenLayout
            header={
                <FullScreenHeader
                    title={classItem.title}
                    onBack={() => navigate(backToClassDetailPath())}
                    centerContent={renderCenterContent()}
                    rightContent={renderRightContent()}
                />
            }
        >
            <div className="flex-1 bg-white overflow-y-auto">
                <div className="max-w-[1200px] mx-auto px-12 py-8">
                    {/* 헤더 */}
                    <div className="flex items-center justify-between mb-6">
                        <h1 className="text-2xl font-bold text-gray-900">{t('statistics.title')}</h1>
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={handleExcelDownload}
                            disabled={isDownloadingExcel}
                        >
                            <FileSpreadsheet size={16} className="text-blue-500" />
                            {isDownloadingExcel ? t('statistics.downloading') : t('statistics.excelDownload')}
                        </Button>
                    </div>

                    {/* 통계 카드 */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="text-sm text-gray-600 mb-2">{t('statistics.totalParticipants')}</div>
                            <div className="text-2xl font-bold text-gray-900">{stats.totalParticipants}{t('units.people')}</div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="text-sm text-gray-600 mb-2">{t('statistics.pass')}</div>
                            <div className="text-2xl font-bold text-gray-900">
                                {stats.passCount}{t('units.people')} ({stats.passRate}%)
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="text-sm text-gray-600 mb-2">{t('statistics.totalPracticeCount')}</div>
                            <div className="text-2xl font-bold text-gray-900">{stats.totalPlayCount}{t('units.times')}</div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="text-sm text-gray-600 mb-2">{t('statistics.totalPracticeTime')}</div>
                            <div className="text-2xl font-bold text-gray-900">{stats.totalPlayTime}</div>
                        </div>
                    </div>

                    {/* 서브 탭 */}
                    <div className="flex gap-2 mb-4">
                        <button
                            onClick={() => setStatisticsTab('participants')}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${
                                statisticsTab === 'participants'
                                    ? 'text-blue-600 border-b-2 border-blue-600'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            {t('statistics.participantsTab')}
                        </button>
                        <button
                            onClick={() => setStatisticsTab('scenarios')}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${
                                statisticsTab === 'scenarios'
                                    ? 'text-blue-600 border-b-2 border-blue-600'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            {t('statistics.scenariosTab')}
                        </button>
                    </div>

                    {/* 테이블 */}
                    {statisticsTab === 'participants' && (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                                            {t('statistics.participantNameId')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.affiliation')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.jobTitle')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.practiceCount')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.practiceTime')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.participatedCompletedScenarios')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.highestScore')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.status')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isParticipantStatsLoading ? (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                                                {t('statistics.loadingData')}
                                            </td>
                                        </tr>
                                    ) : isParticipantStatsError ? (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-8 text-center text-sm text-red-500">
                                                {t('statistics.loadError')}
                                            </td>
                                        </tr>
                                    ) : participants.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                                                {t('statistics.noParticipantData')}
                                            </td>
                                        </tr>
                                    ) : (
                                        participants.map((participant, index) => (
                                            <tr
                                                key={index}
                                                className="border-b border-gray-100 hover:bg-gray-50"
                                            >
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    {participant.user_name} ({participant.user_email})
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {participant.affiliation || '—'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {participant.job_title || '—'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {participant.play_count}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {formatPlayTime(participant.total_play_time, t)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {participant.participated_scenario_count} / {participant.completed_scenario_count}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {participant.highest_score ?? '—'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {participant.status || '—'}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {statisticsTab === 'scenarios' && (
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                                            {t('statistics.scenario')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.mode')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.practiceCount')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.practiceTime')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.practiceParticipants')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.completionRate')}
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            {t('statistics.averageScore')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isScenarioStatsLoading ? (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                                                {t('statistics.loadingData')}
                                            </td>
                                        </tr>
                                    ) : isScenarioStatsError ? (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-8 text-center text-sm text-red-500">
                                                {t('statistics.loadError')}
                                            </td>
                                        </tr>
                                    ) : !scenarioStatsData?.contentList || scenarioStatsData.contentList.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                                                {t('statistics.noScenarioData')}
                                            </td>
                                        </tr>
                                    ) : (
                                        scenarioStatsData.contentList.map((scenario, index) => (
                                            <tr
                                                key={index}
                                                className="border-b border-gray-100 hover:bg-gray-50"
                                            >
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    {scenario.scenario_name}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {scenario.mode_value_title || '—'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {scenario.play_count}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {formatPlayTime(scenario.total_play_time, t)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {scenario.participant_count}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {scenario.completion_rate ?? 0}%
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {scenario.avg_score ?? 0}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </FullScreenLayout>
    );
}
